import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Megaphone, Loader2, CheckCircle2, Upload, FileCheck2 } from "lucide-react";

function DocUpload({ label, testid, value, onDone }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const src = value ? (value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : null;
  const isPdf = value && value.toLowerCase().endsWith(".pdf");
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/mentors/upload-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.url);
      toast.success(`${label} telechaje`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading} data-testid={testid}
        className={`relative w-full h-24 rounded border overflow-hidden flex flex-col items-center justify-center gap-1 text-[11px] transition-colors px-1.5 text-center ${value ? "border-[#00F593]/60 text-[#00F593]" : "border-dashed border-[#333] bg-[#0A0A0A] text-[#888] hover:border-[#00F593]/40"}`}>
        {value && !isPdf && <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid={`${testid}-preview`} />}
        <div className="relative z-10 flex flex-col items-center justify-center gap-1 bg-black/40 rounded px-1.5 py-1">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : value ? <FileCheck2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          <span className="leading-tight text-white">{value ? (isPdf ? "PDF ✓" : "Chanje foto") : label}</span>
        </div>
      </button>
    </div>
  );
}

function ApplyCard({ variant }) {
  const isMentor = variant === "mentor";
  const [form, setForm] = useState(
    isMentor
      ? { name: "", email: "", phone: "", experience: "", message: "", payout_email: "" }
      : { name: "", email: "", phone: "", platform: "", handle: "", followers: "", payout_email: "", message: "" });
  const [docs, setDocs] = useState({ id_front_url: "", id_back_url: "", account_proof_url: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const accent = isMentor ? "#00F593" : "#1C8CFF";

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Non ak imèl obligatwa"); return; }
    if (!docs.id_front_url || !docs.id_back_url || (isMentor && !docs.account_proof_url)) {
      toast.error(isMentor
        ? "Tanpri telechaje tout 3 dokiman yo (ID fas, ID do, foto kont reyèl la)."
        : "Tanpri telechaje 2 foto ID yo (fas + do)."); return;
    }
    setBusy(true);
    try {
      await api.post(isMentor ? "/mentors/apply" : "/partners/apply", { ...form, ...docs });
      setDone(true); toast.success("Aplikasyon resevwa!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const F = (k, ph, type = "text") => (
    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} type={type}
      data-testid={`${variant}-apply-${k}`} placeholder={ph}
      className="w-full bg-[#0A0A0A] border border-[#222] h-11 px-3.5 text-sm outline-none focus:border-white/30 transition-colors text-white placeholder:text-[#555]" />
  );

  return (
    <div className="border border-[#222] bg-[#0F0F0F] p-6 flex flex-col" data-testid={`${variant}-apply-card`}>
      <div className="flex items-center gap-2.5 mb-1.5">
        <div className="h-9 w-9 flex items-center justify-center rounded-sm" style={{ backgroundColor: accent }}>
          {isMentor ? <GraduationCap className="h-5 w-5 text-black" /> : <Megaphone className="h-5 w-5 text-white" />}
        </div>
        <h3 className="font-heading text-xl font-bold">{isMentor ? "Vin yon Mentor" : "Vin yon Patnè"}</h3>
      </div>
      <p className="text-[13px] text-[#888] leading-relaxed mb-4">
        {isMentor
          ? "Tradè Gold ki fè prèv? Poste siyal an dirèk, dirije sal analiz & gide kliyan — touche 10% sou chak kliyan ou pote."
          : "Gen yon odyans? Fè pwomosyon Smart FX sou TikTok, Instagram, YouTube oswa Facebook ak lyen referral ou epi touche sou chak kliyan ki peye."}
      </p>

      {done ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid={`${variant}-apply-done`}>
          <CheckCircle2 className="h-10 w-10 mb-3" style={{ color: accent }} />
          <h4 className="font-heading font-bold">Aplikasyon resevwa!</h4>
          <p className="text-xs text-[#888] mt-1.5">Ekip nou an ap revize l epi kontakte w pa imèl.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            {F("name", "Non konplè *")}
            {F("email", "Imèl *", "email")}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {F("phone", "Telefòn / WhatsApp")}
            {F("payout_email", "Imèl pou peye")}
          </div>
          {isMentor && (
            F("experience", "Eksperyans trading")
          )}
          {!isMentor && (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                {F("platform", "Platfòm (TikTok…)")}
                {F("followers", "Followers (egz. 85k)")}
              </div>
              {F("handle", "@handle oswa lyen chanèl")}
            </>
          )}
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid={`${variant}-apply-message`} rows={2}
            placeholder={isMentor ? "Estrateji ou & istwa rezilta ou…" : "Pale nou de odyans ou…"}
            className="w-full bg-[#0A0A0A] border border-[#222] p-3.5 text-sm outline-none focus:border-white/30 transition-colors text-white placeholder:text-[#555]" />

          <div className="border-t border-[#222] pt-3">
            <div className="font-mono-term text-[10px] uppercase tracking-widest mb-1" style={{ color: accent }}>Dokiman Verifikasyon (obligatwa)</div>
            <p className="text-[11px] text-[#888] leading-relaxed mb-2.5">
              {isMentor
                ? <>2 foto ID (fas + do) + yon foto kont reyèl ou a ki montre <b className="text-white">istwa & montan total</b> (omwen <b style={{ color: accent }}>$10,000</b> sou <b style={{ color: accent }}>30 jou</b>).</>
                : <>2 foto ID (fas + do) pou verifye idantite ou.</>}
            </p>
            <div className={`grid ${isMentor ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
              <DocUpload label="ID Fas" testid={`${variant}-doc-id-front`} value={docs.id_front_url} onDone={(u) => setDocs((d) => ({ ...d, id_front_url: u }))} />
              <DocUpload label="ID Do" testid={`${variant}-doc-id-back`} value={docs.id_back_url} onDone={(u) => setDocs((d) => ({ ...d, id_back_url: u }))} />
              {isMentor && (
                <DocUpload label="Kont Reyèl" testid={`${variant}-doc-account`} value={docs.account_proof_url} onDone={(u) => setDocs((d) => ({ ...d, account_proof_url: u }))} />
              )}
            </div>
          </div>

          <button onClick={submit} disabled={busy} data-testid={`${variant}-apply-submit`}
            className="w-full h-11 font-semibold transition-colors flex items-center justify-center gap-2 hover:opacity-90"
            style={{ backgroundColor: accent, color: isMentor ? "#000" : "#fff" }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Soumèt Aplikasyon"}
          </button>
        </div>
      )}
    </div>
  );
}

export function JoinTeamSection() {
  return (
    <section id="join" className="border-t border-[#222] bg-[#080808]" data-testid="join-team-section">
      <div className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-10">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Travay Avèk Nou</span>
          <h2 className="font-heading text-4xl font-bold tracking-tight mt-3">Rantre nan Ekip Smart FX la</h2>
          <p className="text-[#888] mt-3 max-w-2xl mx-auto">Aplike kòm yon Mentor pwofesyonèl oswa kòm yon Patnè / enfliyansè. Ekip nou an revize chak aplikasyon pèsonèlman.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="apply-mentor-section">
          <ApplyCard variant="mentor" />
          <ApplyCard variant="partner" />
        </div>
      </div>
    </section>
  );
}
